import { useEffect } from 'react'

import { useLazyQuery } from '@apollo/client'
import { IconChevronRight } from '@tabler/icons-react'
import styled from 'styled-components'

import { useDocumentFolders } from '@/hooks/useDocumentFolders'
import { GET_DOCUMENT_FOLDER_HIERARCHY } from '@/modules/documents/graphQL'

import {
  DocumentFolderHierarchyUiQuery,
  DocumentFolderHierarchyUiQueryVariables,
} from 'types/api.graphql'

type Props = {
  onFolderClick?: () => void
}

const Crumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`

const Crumb = styled.span`
  ${({ theme }) => theme.font.label02}
  font-weight: 600;
  cursor: pointer;

  &:hover,
  &:focus {
    text-decoration: underline;
  }

  &:not(:last-child) {
    color: ${({ theme }) => theme.colour.primary._700};
  }
`

export const TableFolderBreadcrumbs: React.FC<Props> = ({ onFolderClick }) => {
  const { folderId, setFolderId } = useDocumentFolders(false)

  const handleFolderClick = (newFolderId?: string) => {
    setFolderId(newFolderId)
    onFolderClick?.()
  }

  const [getHierarchy, { data }] = useLazyQuery<
    DocumentFolderHierarchyUiQuery,
    DocumentFolderHierarchyUiQueryVariables
  >(GET_DOCUMENT_FOLDER_HIERARCHY)

  useEffect(() => {
    if (folderId) {
      getHierarchy({ variables: { id: folderId } })
    }
  }, [folderId, getHierarchy])

  if (!folderId || !data?.documentFolderHierarchy?.length) return null

  return (
    <Crumbs>
      <Crumb onClick={() => handleFolderClick()}>Home</Crumb>

      <IconChevronRight size={12} />

      {data.documentFolderHierarchy.map((folder, index) => (
        <>
          <Crumb key={folder.id} onClick={() => handleFolderClick(folder.id)}>
            {folder.name}
          </Crumb>

          {index !== data.documentFolderHierarchy.length - 1 && (
            <IconChevronRight size={12} />
          )}
        </>
      ))}
    </Crumbs>
  )
}
